/*
 * Everything that touches Office.js / the Word JavaScript API lives here, so
 * the pane itself stays a plain React component that can be tested with this
 * module mocked.
 */
import { TAG_KIND, findTags } from "./wordAddinModel";

const OFFICE_JS = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";

let readyPromise = null;

// Loads Office.js once and resolves with { host, platform } (host is null
// when the page is opened outside Office, e.g. in a normal browser tab).
export const loadOffice = () => {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise((resolve, reject) => {
    // Office.js replaces history.pushState/replaceState with no-ops inside
    // some hosts, which breaks React Router. Keep the originals.
    const { pushState, replaceState } = window.history;
    const restoreHistory = () => {
      if (typeof window.history.pushState !== "function") window.history.pushState = pushState;
      if (typeof window.history.replaceState !== "function") window.history.replaceState = replaceState;
    };
    const finish = () => {
      restoreHistory();
      if (!window.Office?.onReady) {
        resolve({ host: null, platform: null });
        return;
      }
      window.Office.onReady((info) => {
        restoreHistory();
        resolve({ host: info?.host || null, platform: info?.platform || null });
      });
    };
    if (window.Office?.onReady) {
      finish();
      return;
    }
    const script = document.createElement("script");
    script.src = OFFICE_JS;
    script.async = true;
    script.onload = finish;
    script.onerror = () => {
      restoreHistory();
      reject(new Error("Office.js could not be loaded."));
    };
    document.head.appendChild(script);
  });
  return readyPromise;
};

export const isWord = (info) => Boolean(info?.host) && String(info.host).toLowerCase() === "word";

export const documentUrl = () => window.Office?.context?.document?.url || "";

const run = (fn) => window.Word.run(fn);

// Insert a tag at the cursor (replacing any selection). Signature tags are
// written in white so they never print; "reveal" highlights make them visible.
export const insertAtCursor = async (text, { color = null, highlight = null } = {}) =>
  run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("font/color");
    await context.sync();
    const previousColor = selection.font.color && selection.font.color !== "" ? selection.font.color : "#000000";

    const inserted = selection.insertText(text, "Replace");
    if (color) inserted.font.color = color;
    if (highlight) inserted.font.highlightColor = highlight;

    // A following space in the original colour, so typing continues normally
    // instead of inheriting white text or a highlight.
    const trailing = inserted.insertText(" ", "After");
    trailing.font.color = previousColor;
    trailing.font.highlightColor = null;
    trailing.getRange("End").select();
    await context.sync();
  });

// Body plus every section's primary header and footer — signature tags are
// often parked in a footer.
const textAreas = (context) => {
  const body = context.document.body;
  const sections = context.document.sections;
  sections.load("items");
  return { body, sections };
};

const collectAreas = async (context) => {
  const { body, sections } = textAreas(context);
  await context.sync();
  const areas = [body];
  sections.items.forEach((section) => {
    areas.push(section.getHeader("Primary"));
    areas.push(section.getFooter("Primary"));
  });
  return areas;
};

// Every tag in the document, with kind. Reads paragraph text and matches in
// JS, so odd characters never have to survive Word's wildcard syntax.
export const scanTags = async () =>
  run(async (context) => {
    const areas = await collectAreas(context);
    const paragraphSets = areas.map((area) => {
      const paragraphs = area.paragraphs;
      paragraphs.load("items/text");
      return paragraphs;
    });
    await context.sync();
    const tags = [];
    paragraphSets.forEach((set) => set.items.forEach((p) => tags.push(...findTags(p.text))));
    return tags;
  });

// Highlight (or un-highlight) every tag, by literal search for each distinct
// tag string. `colours` maps kind -> Office highlight name.
export const setReveal = async (on, colours) =>
  run(async (context) => {
    const areas = await collectAreas(context);
    const paragraphSets = areas.map((area) => {
      const paragraphs = area.paragraphs;
      paragraphs.load("items/text");
      return paragraphs;
    });
    await context.sync();

    const distinct = new Map();
    paragraphSets.forEach((set) =>
      set.items.forEach((p) => findTags(p.text).forEach((t) => distinct.set(t.text, t.kind)))
    );
    if (!distinct.size) return 0;

    const results = [];
    areas.forEach((area) => {
      distinct.forEach((kind, text) => {
        // Word's search string is capped at 255 characters.
        if (text.length > 255) return;
        const found = area.search(text, { matchCase: true, matchWildcards: false });
        found.load("items");
        results.push({ found, kind });
      });
    });
    await context.sync();

    let touched = 0;
    results.forEach(({ found, kind }) => {
      found.items.forEach((range) => {
        // colours is keyed the way the server sends it: fields / signatures / conditionals.
        const colour = colours[TAG_KIND[kind]?.highlightKey] || null;
        range.font.highlightColor = on ? colour : null;
        touched += 1;
      });
    });
    await context.sync();
    return touched;
  });

// Open a same-origin page in an Office dialog and resolve with the first
// message it sends back (used for signing in).
export const openDialog = (url, { height = 60, width = 35 } = {}) =>
  new Promise((resolve, reject) => {
    const ui = window.Office?.context?.ui;
    if (!ui?.displayDialogAsync) {
      reject(new Error("Dialogs are not available here."));
      return;
    }
    ui.displayDialogAsync(url, { height, width, promptBeforeOpen: false }, (result) => {
      if (result.status !== window.Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || "Could not open the sign-in window."));
        return;
      }
      const dialog = result.value;
      dialog.addEventHandler(window.Office.EventType.DialogMessageReceived, (arg) => {
        try {
          dialog.close();
        } catch (e) {
          // already closed
        }
        resolve(arg.message);
      });
      dialog.addEventHandler(window.Office.EventType.DialogEventReceived, () => {
        reject(new Error("The sign-in window was closed."));
      });
    });
  });

export const messageParent = (message) => {
  window.Office?.context?.ui?.messageParent?.(message);
};
